import { useEffect, useMemo, useState } from 'react';
import { ApiCallError } from '../api/client';
import { sysApi, type SysAssetCategory, type SysAssetFile } from '../api/sysadmin';

const FRONTEND_BASE_FALLBACK =
  (import.meta.env.VITE_FRONTEND_BASE as string | undefined) ||
  (typeof window !== 'undefined'
    ? window.location.origin.replace(/\/?$/, '').replace('admin', 'app')
    : '');

// Friendly labels for known group keys. Anything not listed here renders as
// "<key>" — the goal is just to make species names recognisable in Chinese.
const GROUP_LABELS: Record<string, { icon: string; label: string }> = {
  // pet species
  hamster:  { icon: '🐹', label: '仓鼠' },
  rabbit:   { icon: '🐰', label: '兔子' },
  dog:      { icon: '🐕', label: '狗狗' },
  dragon:   { icon: '🐉', label: '神兽' },
  axolotl:  { icon: '🦎', label: '蝾螈' },
  capybara: { icon: '🦫', label: '水豚' },
  hedgehog: { icon: '🦔', label: '刺猬' },
  koala:    { icon: '🐨', label: '考拉' },
  otter:    { icon: '🦦', label: '水獭' },
  parrot:   { icon: '🦜', label: '鹦鹉' },
  // sound non-pet
  music:    { icon: '🎵', label: '背景音乐' },
  scenes:   { icon: '🌳', label: '场景环境音' },
  pets:     { icon: '🐾', label: '其它宠物' },
  // items
  food:     { icon: '🍎', label: '食物' },
  toy:      { icon: '🧸', label: '玩具' },
  furn:     { icon: '🪑', label: '家具' },
  prop:     { icon: '🎀', label: '道具' },
  floor:    { icon: '⬜', label: '地板' },
  wall:     { icon: '🧱', label: '墙面' },
  invest:   { icon: '📈', label: '投资' },
  // misc
  meadow:   { icon: '🌿', label: '草地' },
  scene:    { icon: '🏞️', label: '场景' },
  fx:       { icon: '✨', label: '特效' },
  icon:     { icon: '🟦', label: '图标' },
  bldg:     { icon: '🏛️', label: '建筑' },
  land:     { icon: '🏞️', label: '土地' },
  README:   { icon: '📄', label: '说明' },
};

function groupMeta(key: string) {
  return GROUP_LABELS[key] || { icon: '📁', label: key };
}

export default function SysAdminAssetsPage() {
  const [base, setBase] = useState<string>('');
  const [categories, setCategories] = useState<SysAssetCategory[]>([]);
  const [generatedAt, setGeneratedAt] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [active, setActive] = useState<string>('sprites');
  const [filter, setFilter] = useState<string>('');
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [preview, setPreview] = useState<SysAssetFile | null>(null);

  useEffect(() => {
    sysApi.listAssets()
      .then(r => {
        setBase(r.base || FRONTEND_BASE_FALLBACK);
        setCategories(r.categories);
        setGeneratedAt(r.generated_at);
      })
      .catch(e => setErr(e instanceof ApiCallError ? e.message : '加载失败'))
      .finally(() => setLoading(false));
  }, []);

  const cat = useMemo(
    () => categories.find(c => c.key === active) || categories[0],
    [categories, active],
  );

  const filtered = useMemo(() => {
    if (!cat) return [];
    const q = filter.trim().toLowerCase();
    let files = cat.files;
    if (q) files = files.filter(f => f.url.toLowerCase().includes(q));
    if (activeGroup) files = files.filter(f => f.group === activeGroup);
    return files;
  }, [cat, filter, activeGroup]);

  // Group counts for the active category (respects search filter so a search
  // of "happy" inside sounds shows you how many "happy" each species has).
  const groupCounts = useMemo(() => {
    if (!cat) return [] as Array<{ group: string; count: number }>;
    const q = filter.trim().toLowerCase();
    const m = new Map<string, number>();
    for (const f of cat.files) {
      if (q && !f.url.toLowerCase().includes(q)) continue;
      m.set(f.group, (m.get(f.group) || 0) + 1);
    }
    return Array.from(m.entries())
      .map(([group, count]) => ({ group, count }))
      .sort((a, b) => b.count - a.count || a.group.localeCompare(b.group));
  }, [cat, filter]);

  // Reset group selection whenever the active category changes.
  function selectCategory(key: string) {
    setActive(key);
    setFilter('');
    setActiveGroup(null);
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title">🖼️ 资源浏览</div>
        <div className="page-subtitle">
          {generatedAt && `清单生成于 ${new Date(generatedAt).toLocaleString('zh-CN')}`}
          {' · '}重新生成请运行 <code>node tools/gen_asset_manifest.mjs</code> 后重新部署
        </div>
      </div>

      {err && <div className="error-box">{err}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <span className="spinner spinner-lg" />
        </div>
      ) : (
        <>
          <div className="card" style={{ padding: 12 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {categories.map(c => (
                <button
                  key={c.key}
                  className={`btn ${c.key === active ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => selectCategory(c.key)}
                >
                  {c.label} <span style={{ opacity: 0.7 }}>({c.files.length})</span>
                </button>
              ))}
            </div>
            <input
              style={{ marginTop: 12 }}
              placeholder="按文件名过滤（在所有分组里搜索）..."
              value={filter}
              onChange={e => setFilter(e.target.value)}
            />
          </div>

          {/* Group chips for the active category */}
          {cat && groupCounts.length > 1 && (
            <div className="card" style={{ padding: 12 }}>
              <div className="muted" style={{ fontSize: 12, marginBottom: 8, fontWeight: 700 }}>
                按分组筛选：
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  className={`btn ${activeGroup === null ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ padding: '6px 12px', minHeight: 36, fontSize: 13 }}
                  onClick={() => setActiveGroup(null)}
                >
                  全部 ({cat.files.length})
                </button>
                {groupCounts.map(({ group, count }) => {
                  const meta = groupMeta(group);
                  return (
                    <button
                      key={group}
                      className={`btn ${activeGroup === group ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ padding: '6px 12px', minHeight: 36, fontSize: 13 }}
                      onClick={() => setActiveGroup(activeGroup === group ? null : group)}
                    >
                      {meta.icon} {meta.label} ({count})
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {cat && (
            <div className="card">
              <div style={{ fontWeight: 800, marginBottom: 8 }}>
                {cat.label}
                {activeGroup && (
                  <>
                    {' · '}
                    {groupMeta(activeGroup).icon} {groupMeta(activeGroup).label}
                  </>
                )}
                {' '}({filtered.length}/{cat.files.length})
              </div>
              {!base && (
                <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                  ⚠️ 未配置 FRONTEND_BASE — 缩略图可能无法加载，请在 wrangler 设置 <code>FRONTEND_BASE</code>。
                </div>
              )}

              {/* When a single group is selected we render a flat grid; when
                  showing "all", split into labelled sections so kids of names
                  aren't crammed together. */}
              {activeGroup ? (
                <div
                  className="grid"
                  style={{
                    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                    gap: 12,
                  }}
                >
                  {filtered.map(f => (
                    <AssetTile key={f.url} file={f} base={base} onClick={() => setPreview(f)} />
                  ))}
                </div>
              ) : (
                groupCounts.map(({ group }) => {
                  const groupFiles = filtered.filter(f => f.group === group);
                  if (groupFiles.length === 0) return null;
                  const meta = groupMeta(group);
                  return (
                    <div key={group} style={{ marginTop: 12 }}>
                      <div style={{
                        fontWeight: 800, fontSize: 14,
                        padding: '8px 0', borderBottom: '1px solid var(--border)',
                        marginBottom: 10,
                      }}>
                        {meta.icon} {meta.label}{' '}
                        <span className="muted" style={{ fontWeight: 600 }}>({groupFiles.length})</span>
                      </div>
                      <div
                        className="grid"
                        style={{
                          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                          gap: 12,
                        }}
                      >
                        {groupFiles.map(f => (
                          <AssetTile key={f.url} file={f} base={base} onClick={() => setPreview(f)} />
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </>
      )}

      {preview && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
          onClick={() => setPreview(null)}
        >
          <div
            className="card"
            style={{ maxWidth: 600, width: '100%' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, fontSize: 14, wordBreak: 'break-all', marginBottom: 8 }}>
              {preview.url}
            </div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
              {preview.kind} · {fmtSize(preview.size)}
            </div>
            <PreviewBody file={preview} base={base} />
            <div className="flex" style={{ marginTop: 16, justifyContent: 'flex-end' }}>
              <a
                className="btn btn-secondary"
                href={`${base}${preview.url}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                在新标签打开
              </a>
              <button className="btn btn-primary" onClick={() => setPreview(null)}>关闭</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AssetTile({ file, base, onClick }: { file: SysAssetFile; base: string; onClick: () => void }) {
  const url = `${base}${file.url}`;
  const parts = file.url.split('/');
  const name = parts.pop() || file.url;
  // Show the immediate parent dir if it adds disambiguating context (e.g.
  // "hamster/happy.mp3" — useful when the same basename repeats per species).
  const parent = parts.length > 0 ? parts[parts.length - 1] : '';
  return (
    <div
      onClick={onClick}
      style={{
        background: 'white', borderRadius: 12, padding: 8,
        border: '1px solid var(--border)', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
      }}
    >
      <div style={{
        width: '100%', height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#fafaf9', borderRadius: 8, overflow: 'hidden',
      }}>
        {file.kind === 'image' ? (
          <img
            src={url}
            alt={name}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            loading="lazy"
          />
        ) : file.kind === 'sound' ? (
          <span style={{ fontSize: 36 }}>🎵</span>
        ) : file.kind === 'model' ? (
          <span style={{ fontSize: 36 }}>📦</span>
        ) : (
          <span style={{ fontSize: 36 }}>📄</span>
        )}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, textAlign: 'center', wordBreak: 'break-all', lineHeight: 1.3 }}>
        {name}
      </div>
      <div className="muted" style={{ fontSize: 11 }}>
        {parent && <span style={{ marginRight: 4 }}>{parent}/</span>}
        {fmtSize(file.size)}
      </div>
    </div>
  );
}

function PreviewBody({ file, base }: { file: SysAssetFile; base: string }) {
  const url = `${base}${file.url}`;
  if (file.kind === 'image') {
    return (
      <img
        src={url}
        alt={file.url}
        style={{ width: '100%', maxHeight: 400, objectFit: 'contain', background: '#0b0b0b', borderRadius: 8 }}
      />
    );
  }
  if (file.kind === 'sound') {
    return <audio controls src={url} style={{ width: '100%' }} />;
  }
  return <div className="muted">无预览（请在新标签打开）</div>;
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}
